/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Component, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Account, Customer, CustomerAccountItem, InyovaGrowAccountStatus, StepStatus } from '@inyova/models';
import { isCustomer, isNotYetCustomer, sortByStatus } from '@inyova/utils';
import { TranslateService } from '@ngx-translate/core';

import { select, Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import * as AccountActions from '@account/account.actions';
import * as RootActions from '@app/app.actions';
import * as InyovaGrowActions from '@grow/inyova-grow.actions';
import * as fromAccount from '@account/account.reducers';
import * as fromRoot from '@app/app.reducers';

import { LocalStorageKeys } from '@app/app.constants';
import { State } from '@shared/models/State';
import { AuthService } from '@shared/services/auth.service';
import { TrackingService } from '@shared/services/tracking.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-switch-account',
  templateUrl: './switch-account.component.html',
  styleUrls: ['./switch-account.component.scss'],
  standalone: false
})
export class SwitchAccountComponent {
  protected readonly onDestroy$ = new Subject<void>();
  @Output() isAccountMenuOpen = new EventEmitter<boolean>();
  selectedAccount: CustomerAccountItem;
  customer: Customer;
  kidsAccounts: CustomerAccountItem[];
  switchAccountOpened = false;
  account3A: CustomerAccountItem;
  account3B: CustomerAccountItem;
  accountGrow: CustomerAccountItem;
  growAccountStatus = InyovaGrowAccountStatus;
  stepStatus = StepStatus;
  isCIActive = false;

  constructor(
    private store: Store<State>,
    private trackingService: TrackingService,
    private authService: AuthService,
    private translateService: TranslateService,
    private router: Router
  ) {
    this.store.pipe(select(fromAccount.selectCustomer), take(1)).subscribe((customer) => {
      if (!customer || !customer.accounts) {
        return;
      }
      this.customer = customer;
      this.kidsAccounts = customer.accounts
        .filter((account) => account.kind === 'kid')
        .sort(sortByStatus)
        .reverse();

      this.accountGrow = this.customer.temporary_accounts.find((account) => account.kind === 'grow');
      this.account3A = this.findAccount('3a');
      this.account3B = this.findAccount('3b');
    });
    this.store.pipe(select(fromRoot.selectSelectedAccount), takeUntil(this.onDestroy$)).subscribe((account) => {
      this.selectedAccount = account;
    });

    this.store.pipe(select(fromRoot.selectCrowdInvestorAccountActive), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.isCIActive = value;
    });

    this.authService.getLastAuthToken().subscribe(noop);
  }

  findAccount(type: string): CustomerAccountItem {
    return this.customer.accounts.find((account) => account.kind === type);
  }

  createAccount(type: string): void {
    this.trackingService.trackActivity(`[Link] Create a new ${type} account`);
    const url = `${environment.customersApp[this.customer.app_location]}/?customer_token=${localStorage.getItem(
      LocalStorageKeys.AUTHENTICATION_TOKEN
    )}&redirect=/new-account/${type}${type === 'grow' && this.accountGrow !== undefined && this.accountGrow.id ? `&selected_account=${this.accountGrow.id}` : ``}`;
    Browser.open({ url }).finally(noop);
    void Browser.addListener('browserFinished', () => {
      this.router.navigate(['/public/login']).finally(noop);
    });
  }

  switchAccount(targetAccount: CustomerAccountItem): void {
    let account: CustomerAccountItem;

    if (targetAccount.kind === '3b' || targetAccount.kind === '3a') {
      account = this.customer.temporary_accounts.find((item) => item.kind === targetAccount.kind);
    } else {
      // it's kid account find account by id
      account = this.customer.accounts.find((item) => item.id === (targetAccount as CustomerAccountItem).id);
    }

    // if the account step status < 5 redirect web-app
    if (isNotYetCustomer(account)) {
      this.trackingService.trackActivity(`[Button] Account switch - Redirect to Web app - ${account.kind}`);
      const url = `${environment.customersApp[this.customer.app_location]}/?customer_token=${localStorage.getItem(
        LocalStorageKeys.AUTHENTICATION_TOKEN
      )}&selected_account=${account.id}`;
      Browser.open({ url }).finally(noop);
      void Browser.addListener('browserFinished', () => {
        this.router.navigate(['/public/login']).finally(noop);
      });
      return;
    }

    // don't trigger if the user is the same
    if (this.selectedAccount?.id !== account.id || this.isCIActive) {
      this.trackingService.trackActivity(`[Button] Account switch - Select ${account.kind} account`);

      this.store.dispatch(
        AccountActions.setSwitchAccountLoading({
          show: true,
          message: this.translateService.instant(`SHARED.accountSwitchSpinner.${account.kind}`, { kidsName: account.owner_name })
        })
      );

      this.store.dispatch(AccountActions.getCurrentAccount({ id: account.id }));
      this.store.dispatch(RootActions.setSelectedAccount({ account }));
      //reset others
      this.store.dispatch(RootActions.setCrowdInvestorAccountStatus({ active: false }));
      this.router.navigate(['/tabs/performance']);
    }
    // update menu state
    this.toggleMenu();
  }

  switchAccountToCI(): void {
    if (this.isCIActive) {
      return;
    }

    this.trackingService.trackActivity(`[Button] Account switch - Select ci account`);
    this.store.dispatch(
      AccountActions.setSwitchAccountLoading({ show: true, message: this.translateService.instant(`SHARED.accountSwitchSpinner.ci`) })
    );
    this.store.dispatch(RootActions.setCrowdInvestorAccountStatus({ active: true }));
    this.router.navigate(['/tabs/performance']);

    setTimeout(() => {
      this.store.dispatch(AccountActions.setSwitchAccountLoading({ show: false, message: '' }));
    }, 400);

    this.toggleMenu();
  }

  switchAccountToGrow(): void {
    if (this.selectedAccount?.kind === 'grow' && !this.isCIActive) {
      return;
    }

    this.trackingService.trackActivity(`[Button] Account switch - Select grow account`);
    this.store.dispatch(
      AccountActions.setSwitchAccountLoading({ show: true, message: this.translateService.instant(`SHARED.accountSwitchSpinner.grow`) })
    );
    this.store.dispatch(RootActions.setCrowdInvestorAccountStatus({ active: false }));
    this.store.dispatch(InyovaGrowActions.getInyovaGrowAccount());

    if (isCustomer(this.accountGrow)) {
      this.store.dispatch(RootActions.setSelectedAccount({ account: this.accountGrow }));
    }

    const target = this.accountGrow.step_status >= 2 ? '/tabs/grow-performance' : '/grow/risk';
    this.router.navigate([target]);

    setTimeout(() => {
      this.store.dispatch(AccountActions.setSwitchAccountLoading({ show: false, message: '' }));
    }, 400);

    this.toggleMenu();
  }

  toggleMenu(): void {
    this.trackingService.trackActivity(`[Button] ${this.switchAccountOpened ? 'Close' : 'Open'} Account switch`);
    this.switchAccountOpened = !this.switchAccountOpened;
    this.isAccountMenuOpen.emit(this.switchAccountOpened);
  }

  createKidsAccount(): void {
    this.trackingService.trackActivity('[Link] Y4K - Create a new kids account');
    const url = `${environment.customersApp[this.customer.app_location]}/?customer_token=${localStorage.getItem(
      LocalStorageKeys.AUTHENTICATION_TOKEN
    )}&redirect=kids,register`;
    Browser.open({ url }).finally(noop);
    void Browser.addListener('browserFinished', () => {
      this.router.navigate(['/public/login']).finally(noop);
    });
  }

  isAlreadyCustomer(account: Account | CustomerAccountItem): boolean {
    return isCustomer(account);
  }

  isNotYetCustomer(account: Account | CustomerAccountItem): boolean {
    return isNotYetCustomer(account);
  }
}
